import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { FormGroup, Label } from 'reactstrap';
import Select from 'react-select';
import { useFormik } from 'formik';
import * as yup from 'yup';
import { setPaymentMethod, setPaymentStatus, selectCart, setTaxesAndDeposits, clearCart, setDiscountAmount,} from 'slices/booking/cartSlice';
import { resetBookingForm, selectBooking } from 'slices/booking/bookingSlice';
import { toast } from 'react-toastify';
import { calculateBookingTotals } from './calculateBookingTotals';
import { createBooking, updateBooking } from 'helpers/API Calls/Operations/booking';
import { useNavigate, useParams } from 'react-router-dom';
import { checkDiscountCode } from 'helpers/API Calls/Operations/discounts';
import { AccessibilityOption, BookingRequestData } from 'types/booking';
interface BookingStatusValues {
  payment_status: string;
  payment_method: string;
  discount_code?: string;
  discount_amount?: number;
}

function PaymentSection() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { id } = useParams();
  const cartData = useSelector(selectCart);
  const bookingData = useSelector(selectBooking);

  // console.log('PaymentSection cartData:', cartData);
  // console.log('PaymentSection bookingData:', bookingData);
  // const isCancelledBooking = bookingData?.booking_status ? bookingStatuses?.find(bs => (bs.id).toString() === bookingData.booking_status)?.status_slug === 'cancelled' : false;

  const paymentMethodOptions = () => {
    const options = [
      { value: 'cash', label: 'Cash', isDisabled: cartData.isEdit },
      { value: 'stripe', label: 'Card', isDisabled: true }
    ];
    return options;
  };

  const paymentMethodList = React.useMemo(() => paymentMethodOptions(), [cartData.paymentMethod]);

  const formik = useFormik<BookingStatusValues>({
    initialValues: cartData.isEdit ?{
      payment_status: cartData.payment.status_id || '',
      payment_method: cartData.paymentMethod || '',
      discount_code: cartData.discountCode || '',
      discount_amount: cartData.discountAmount || 0,
    } : {
      payment_status: '',
      payment_method: '',
      discount_code: '',
      discount_amount: 0,
    },
    validationSchema: yup.object().shape({
      payment_status: yup.string().required('Please select payment status'),
      payment_method: yup.string().required('Please select payment method'),
      discount_code: yup.string(),
    }),
    onSubmit: async (values: any, { setSubmitting }) => {
      setSubmitting(true);
      await handleBooking();
      setSubmitting(false);
    },
  });
  // Sync form with Redux state
  useEffect(() => {
    if (cartData.paymentMethod !== formik.values.payment_method) {
      formik.setFieldValue('payment_method', cartData.paymentMethod || '');
    }
  }, [cartData.paymentMethod]);

  const handlePaymentStatusChange = (option: any) => {
    const status = option?.value || '';
    formik.setFieldValue('payment_status', status);
    dispatch(setPaymentStatus({status_id: status}));
  };

  const handlePaymentMethodChange = async (option: any) => {
    const newPaymentMethod = option?.value || '';
    formik.setFieldValue('payment_method', newPaymentMethod);
    dispatch(setPaymentMethod({method: newPaymentMethod as 'cash' | 'stripe'}));
  };
  const getDiscountAmount = async () => {
    if (!formik.values.discount_code) {
      dispatch(setDiscountAmount({
        discountAmount : 0, 
        discountCode: null,
      }));
      return 0;
    }

    // If cartData is in edit mode and has a discount amount, return it
    // no edit allowed on discounted bookings
    if( cartData.isEdit && cartData.discountAmount > 0) {
      return Number(cartData.discountAmount);
    }
    formik.setErrors({ discount_code: '' });

    const additionalCharges = bookingData?.accessibility_options?.reduce(
      (sum: number, option: AccessibilityOption) => sum + Number(option?.charges || 0),
      0
    );

    const data : any = {
      code: formik.values.discount_code || '',
      booking_date: cartData.bookingDate,
      products: cartData.items.filter(item => item.type !== 'package').map(item => ({
        product_id: item.id,
        quantity: item.amount,
        item_price: item.price || 0,
      })),
      payment : {
        accessibility_charges: additionalCharges,
        refundable_cleaning_deposit: cartData.cleaningDeposit,
      }
    };

    // Add package data if there's a package item
    const packageItem = cartData.items.find(item => item.type === 'package');
    if (packageItem) {
      data.package = {
        package_id: packageItem.id,
        quantity: 1,
        item_price: packageItem.price || 0,
      };
    }

    const response = await checkDiscountCode(data);
    if (response.success) {
      dispatch(setDiscountAmount({
        discountAmount : response.data.final_discount_amount || 0, 
        discountCode: response.data.code || null,
      }));
      return response.data.final_discount_amount || 0;
    } else {
      dispatch(setDiscountAmount({
        discountAmount : 0, 
        discountCode: null,
      }));
      formik.setErrors({ discount_code: response.message || 'Invalid discount code' });
      return 0;
    }
  }

  const handleBooking = async () => {

    if (cartData.items.length === 0){
      toast.error('Please add items to the cart before booking.');
      return;
    };
    if (!bookingData.customerDetails.email || 
        !bookingData.customerDetails.name || 
        !bookingData.customerDetails.postcode_id || 
        !bookingData.customerDetails.complete_postcode ||
        !bookingData.customerDetails.address_line_1 ||
        !bookingData.customerDetails.town || 
        !bookingData.customerDetails.phone_no_mobile
      ){
      toast.error('Please fill in your contact details before booking.');
      return;
    };
    if (!bookingData.deliveryDetails.venue || 
        !bookingData.deliveryDetails.complete_postcode || 
        !bookingData.deliveryDetails.postcode_id ||
        !bookingData.deliveryDetails.address_line_1 ||
        !bookingData.deliveryDetails.town
      ){
      toast.error('Please fill in your delivery address before booking.');
      return;
    };

    if (!bookingData.booking_status){
      toast.error('Please select a booking status before proceeding.');
      return;
    };

    if (!cartData.bookingDate){
      toast.error('Please select a booking date before proceeding.');
      return;
    };   

    let discountAmount = 0;

    if(formik.values.discount_code) {
      discountAmount = await getDiscountAmount();
    }

    if (formik.errors.discount_code) {
      return
    }
    
     const { totalWithVAT, vatAmount, before_discount_amount } =  calculateBookingTotals(
      {
        subTotal: cartData.subTotal,
        cleaningDeposit: cartData.cleaningDeposit,
        vat: cartData.vat
      },
      bookingData?.accessibility_options || [],
      bookingData?.selectedPostcode?.charges || 0, 
      discountAmount, 
    );

    const reqData: BookingRequestData = {
      booking_date: cartData.bookingDate,
      booking_notes: bookingData.booking_notes,
      operator_notes: bookingData.operator_notes,
      booking_status_id: bookingData.booking_status,
      rental_agreement_signature: null,
      referral_source: bookingData.referral_source || null,
      user_type: 'admin',
      customer: bookingData.customerDetails,
      delivery: bookingData.deliveryDetails,
      products: cartData.items.filter((item) => item.type !== 'package').map(p => ({
        product_id: p.id,
        quantity: p.amount,
        item_price: p.price,
      })),
      accessibility_options: bookingData.accessibility_options,
      payment: {
        discount_code: formik.values.discount_code || null,
        total_amount: totalWithVAT.toFixed(2),
        before_discount_amount: before_discount_amount?.toFixed(2),
        refundable_cleaning_deposit: cartData.cleaningDeposit,
        vat: vatAmount.toFixed(2),
        postcode_charges: (bookingData.selectedPostcode?.charges)?.toFixed(2) || 0,
        payment_method: formik.values.payment_method,
        stripe_token: null,
        payment_status_id: formik.values.payment_status,
        discount_amount: discountAmount ? discountAmount.toFixed(2) : 0,
        deposit: cartData?.depositCollected ? cartData.depositAmount.toFixed(2) : 0,
        deposit_collected: cartData?.depositCollected ? 1 : 0,
      },
    }
    if(cartData.isPackageItem){
      const packageItem = cartData.items.find((item) => item.type === 'package');
      if (!packageItem) return null;
      let packageProducts = packageItem.products || [];     
      if(cartData.package?.direct_products && cartData.package?.direct_products.length > 0){
        packageProducts = [
          ...packageProducts,
          ...cartData.package.direct_products.map(item => (item.id).toString())
        ];
      }
      reqData.package = {
        package_id: packageItem.id,
        quantity: packageItem.amount,
        item_price: packageItem.price,
        products: packageProducts,
      };
    };
    const response = cartData.isEdit ? await updateBooking(id , reqData) : await createBooking(reqData);
    if(response.success){
      dispatch(setTaxesAndDeposits({vat: 0, cleaningDeposit: 0, deposit: 0, isDepositEnabled: false}));
      dispatch(clearCart());
      dispatch(resetBookingForm());
      navigate('/bookings', { state: { booking: response.data } });
    }
    else{
      if (response.error && typeof response.error === 'object') {
        // Display each error message from the error object
        Object.values(response.error).forEach((errorArray: any) => {
          if (Array.isArray(errorArray)) {
            errorArray.forEach((error: string) => {
              toast.error(error);
            });
          }
        });
      } else {
        // Fallback to showing the general message if no structured errors
        toast.error(response.message || 'Failed to create booking');
      }
      return;
    }
  };

  useEffect(() => {
    const calculateDiscount = async () => {
      await getDiscountAmount()
    }
    calculateDiscount(); 
  }, [cartData?.items, 
      bookingData?.selectedPostcode, 
      bookingData?.accessibility_options, 
  ]);

  // console.log('paid_amount', cartData.paid_amount)
  // const isRefundable = cartData.isEdit && cartData.paymentMethod === 'stripe'  && cartData.paid_amount > 0;
  // && bookingData.bookingStatuses.find(status => status.id == formik.values.payment_status)?.slug === 'paid'

  // const [refundModal, setRefundModal] = React.useState<boolean>(false);

  return (
    <div className="booking-status-section">
      <div className="payment-method-container mb-4">
        <FormGroup>
          <Label for="payment_method">Payment Method <span className="text-danger">*</span></Label>
          <Select
            id="payment_method"
            name="payment_method"
            options={paymentMethodList}
            value={paymentMethodList.find(option => option.value === formik.values.payment_method) || null}
            onChange={handlePaymentMethodChange}
            onBlur={formik.handleBlur}
            placeholder="Select payment method"
            classNamePrefix="select"
            isDisabled={(cartData.paymentMethod === 'stripe' && cartData.payment.isSuccessful) || cartData.isEdit}
          />
          {formik.touched.payment_method && formik.errors.payment_method && (
            <div className="text-danger mt-1">{formik.errors.payment_method}</div>
          )}
        </FormGroup>
      </div>

      <FormGroup>
        <Label for="payment_status">Payment Status <span className="text-danger">*</span></Label>
        <Select
          id="payment_status"
          name="payment_status"
          options={bookingData.bookingStatuses
            .filter(status => status.type === 'payment_status')
            .map(status => ({
              value: status.id.toString(),
              label: status.name,
            }))}
          value={bookingData.bookingStatuses
            .filter(status => status.type === 'payment_status')
            .map(status => ({
              value: status.id.toString(),
              label: status.name,
            }))
            .find(option => option.value === formik.values.payment_status) || null}
          onChange={handlePaymentStatusChange}
          onBlur={formik.handleBlur}
          placeholder="Select payment status"
          classNamePrefix="select"
          isDisabled={cartData.isEdit && formik.values.payment_method === 'stripe'}
        />
        {formik.touched.payment_status && formik.errors.payment_status && (
          <div className="text-danger mt-1">{formik.errors.payment_status}</div>
        )}
      </FormGroup>

      <FormGroup>
        <Label for="discount_code">Discount Code</Label>
        <div className="input-group auth-pass-inputgroup">
        <input
          id="discount_code"
          name="discount_code"
          type="text"
          className="form-control"
          value={formik.values.discount_code}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          placeholder="Enter discount code"
          disabled={cartData.isEdit}
        />
        <button onClick={() => {
          if (!!formik.values.discount_code) {getDiscountAmount();}
          else {toast.error('Please enter a discount code to apply.');}
        }}
        className="btn btn-light " 
        type="button" id="password-addon"
        disabled={cartData.isEdit}
        >
          <i className="mdi mdi-content-save-all-outline"></i>
        </button>
        </div>
        {formik.touched.discount_code && formik.errors.discount_code && (
          <div className="text-danger mt-1">{formik.errors.discount_code}</div>
        )}
      </FormGroup>

      <div className='text-end d-flex gap-3 justify-content-between align-items-center'>
        <button 
          className={`btn btn-primary w-100`}
          type='button' 
          disabled={
            cartData.items.length === 0 || 
            !cartData.bookingDate || 
            formik.isSubmitting
          } 
          onClick={() => formik.handleSubmit()}>
          {formik.isSubmitting
            ? cartData.isEdit
              ? 'Updating Booking...'
              : 'Creating Booking...'
            : cartData.isEdit
              ? 'Update Booking'
              : 'Create Booking'
          }
        </button>
      </div>
      {/* {refundModal && (
        <RefundPayment onCloseClick={() => setRefundModal(false)} />
      )} */}
    </div>
  );
}

export default PaymentSection;
